import { CJEvent } from '@teetsh/app/src/types/event';
import _sortBy from 'lodash-es/sortBy';
import { areTimeEquals } from '@teetsh/app/src/utils/time';
import _maxBy from 'lodash-es/maxBy';
import { groupOverlappingEvents } from './groupOverlappingEvents';
import { groupEventsByClassroomIds } from './groupEventsByClassroomIds';
import { groupEventsByContiguity } from './groupEventsByContiguity';

function groupLineEvents(lineEvents: CJEvent[]): CJEvent[][] {
  if (lineEvents.length === 0) {
    return [];
  }
  // if all the events of this line have the same start and end, then we put each event in a column (ateliers)
  const shouldGroupByStartAndEnd = lineEvents.every(
    ev =>
      areTimeEquals(ev.start, lineEvents[0].start) &&
      areTimeEquals(ev.end, lineEvents[0].end),
  );
  if (shouldGroupByStartAndEnd) {
    return lineEvents.map(ev => [ev]);
  }

  // if all the events of this line don't have the same classroomIds, then we group by classroomIds
  const shouldGroupByClassroomIds =
    new Set(lineEvents.map(ev => ev.classroomIds.join('%%'))).size > 1;
  if (shouldGroupByClassroomIds) {
    return groupEventsByClassroomIds(lineEvents);
  }

  // fallback : try to group by contiguity
  return groupEventsByContiguity(lineEvents);
}

function explodeBigLineByStart(events: CJEvent[][]): CJEvent[][][] {
  if (events.length <= 6) {
    return [events];
  }
  const flatEvents = events.flat();
  const eventsByStart = flatEvents.reduce((acc, e) => {
    const key = e.start.getTime().toString();
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(e);
    return acc;
  }, new Map<string, CJEvent[]>());

  return Object.values(eventsByStart).map(groupLineEvents);
}

export function getGroupedEvents(events: CJEvent[]): CJEvent[][][] {
  const lines: CJEvent[][] = groupOverlappingEvents(events);
  const linesWithColumns: CJEvent[][][] = lines
    .map(lineEvents => groupLineEvents(lineEvents))
    .flatMap(columns => explodeBigLineByStart(columns))
    .map(columns => {
      const sortedColumns = _sortBy(columns, elements => {
        const maxElement = _maxBy(elements, e => e.order) || {
          order: 0,
          createdAt: new Date(1999, 1, 1),
          classroomOrder: 0,
        };
        return [
          maxElement.order,
          maxElement.classroomOrder,
          maxElement.createdAt.getTime(),
        ];
      });
      return sortedColumns;
    });
  return _sortBy(
    linesWithColumns,
    listOfEvents => listOfEvents[0]?.[0]?.start || new Date(),
  );
}
